import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";
import { RegulifyService } from "../../api/RegulifyService";
import { useLoading } from "../../context/LoadingContext";
import { useClinicalEvaluationStore } from "../../store/useClinicalEvaluationStore";
import { getSelectedStudyProductName } from "../../utils/clinicalStudy";
import { exportTablesToXLSX } from "../../hooks/useExportTableToXLSX";
import {
  SHEET_APPRAISAL,
  SHEET_CLAIMS,
  SHEET_RELEVANT_LITERATURES,
  loadAppraisalExportTable,
  loadClaimsExportTable,
  loadLiteratureExportTable,
} from "../../utils/clinicalEvalExportTables";
import { buildWsUrl } from "../../utils/wsUrl";
import Chart from "../Chart";
import Table from "../Table";
import { fetchAuthSession } from "aws-amplify/auth";

import {
  claimsPopulationSubjectRelvantColumn,
  f1ScoreColumn,
  f2ScoreColumn,
  pmidColumn,
  remarksColumn,
  searchTermColumn,
} from "./Columns";

const columnHelper = createColumnHelper();

const columns = [
  columnHelper.accessor((row) => row.pmid, pmidColumn),
  columnHelper.accessor(
    (row) => row.clinical_evaluation_llm_remarks,
    remarksColumn,
  ),
  columnHelper.accessor((row) => row.searchTerm, searchTermColumn),
  columnHelper.accessor((row) => row.f1_score, f1ScoreColumn),
  columnHelper.accessor((row) => row.f2_score, f2ScoreColumn),
  columnHelper.accessor(
    (row) => row.claims_population_subject_relvant,
    claimsPopulationSubjectRelvantColumn,
  ),
];

const WS_PING_INTERVAL_MS = 20000;

const WS_ERROR_MESSAGE = "There was a problem processing your Clinical Report generation request. Please try again later.";

const UNHANDELLED_ERROR_MESSAGE = "Unhandelled Error fulfilling the request: "

const WS_URL = (token) =>
  buildWsUrl("generate_clinical_eval_report", token);

const getAuthToken = async () => {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
};

const startKeepAlive = (ws) =>
  setInterval(() => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "ping" }));
    }
  }, WS_PING_INTERVAL_MS);

function handleParsedMessage(parsed, { setLoading }) {
  switch (parsed.type) {
    case "error":
      console.error("Processing error:", parsed.message);
      toast.error(`Error: ${parsed.message}`);
      setLoading(false);
      return true;

    case "info":
      console.info("Processing update:", parsed.message);
      toast.info(`Update: ${parsed.message}`);
      return true;

    case "pong":
      console.info("Received ping/pong reply:", parsed.message);
      return true;

    default:
      return false;
  }
}
const Summary = () => {
  const { setLoading } = useLoading();
  const { selectedStudy, selectedAppraisalTables } = useClinicalEvaluationStore(
    useShallow((state) => ({
      selectedStudy: state.selectedStudy,
      selectedAppraisalTables: state.selectedAppraisalTables,
    })),
  );
  const [data, setData] = useState();
  const [study_name, setDataForStudyName] = useState();
  const [pmidMetrics, setPmidMetrics] = useState();
  const [searchTerms, setSearchTerms] = useState();
  const [sorting, setSorting] = useState([]);
  const [relevantPmidPerTermChart, setRelevantPmidPerTermChart] = useState([]);
  const [claimRelevantPmidPerTermChart, setClaimRelevantPmidPerTermChart] = useState([]);
  const [selectedSearchTerm, setSelectedSearchTerm] = useState({ name: "All" });
  const detailsRef = useRef(null);
  const studyName = getSelectedStudyProductName(selectedStudy);
  const hasCharts = pmidMetrics && selectedSearchTerm?.name === "All";

  useEffect(() => {
    const fetchData = async (code) => {
      try {
        if (!studyName) {
          toast.error("studyName is missing!");
          return;
        }

        setLoading(true);
        const response = await RegulifyService.cle_generate_metrics(studyName);
        setData(response.data.metrics);
        setDataForStudyName(response.data.study_name);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData().then();
  }, []);

  useEffect(() => {
    if (data && data.Per_Term_Metrics) {
      const perTermMetrics = data.Per_Term_Metrics;
      setSearchTerms(Object.keys(perTermMetrics));
      const pmidPerTermMetricsChart = Object.keys(perTermMetrics).map((key) => {
        return {
          name: key,
          value: perTermMetrics[key].relevant_articles,
        };
      });

      const claimPmidPerTermChart = Object.keys(perTermMetrics).map((key) => {
        return {
          name: key,
          value: perTermMetrics[key].claims_relevant_articles,
        };
      });
      setRelevantPmidPerTermChart(pmidPerTermMetricsChart);
      setClaimRelevantPmidPerTermChart(claimPmidPerTermChart);
    }
  }, [data]);

  function setupTableData() {
    if (searchTerms) {
      const pmidData = [];
      searchTerms
        .filter(
          (st) =>
            selectedSearchTerm.name === "All" || selectedSearchTerm.name === st,
        )
        .forEach((st) => {
          pmidData.push(
            ...Object.keys(data.Per_Term_Metrics[st].pmid_metrics).map(
              (pmid) => {
                return {
                  pmid,
                  searchTerm: st,
                  ...data.Per_Term_Metrics[st].pmid_metrics[pmid],
                };
              },
            ),
          );
        });

      setPmidMetrics(pmidData);
    }
  }

  useEffect(() => {
    setupTableData();
  }, [searchTerms, selectedSearchTerm]);

  const table = useReactTable({
    data: pmidMetrics || [],
    columns,
    enableColumnPinning: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const getPropertySum = (property) => {
    return selectedSearchTerm.name === "All"
      ? data?.Per_Term_Metrics
        ? Object.values(data.Per_Term_Metrics).reduce(
          (sum, obj) => sum + (obj[property] || 0),
          0,
        )
        : 0
      : selectedSearchTerm[property];
  };

  const handleExportCombinedData = async () => {
    if (!studyName) {
      toast.error("studyName is missing!");
      return;
    }
    setLoading(
      true,
      "Preparing combined export… This may take a few minutes if a step has not been opened yet.",
      "text-green-600",
    );
    try {
      const [literatures, appraisal, claims] = await Promise.all([
        loadLiteratureExportTable(studyName),
        loadAppraisalExportTable(
          studyName,
          selectedAppraisalTables?.f1 === true,
          selectedAppraisalTables?.f2 === true,
        ),
        loadClaimsExportTable(studyName),
      ]);
      const safeName = String(studyName).replace(/[<>:"/\\|?*]+/g, "_");
      exportTablesToXLSX(
        [
          { name: SHEET_RELEVANT_LITERATURES, ...literatures },
          { name: SHEET_APPRAISAL, ...appraisal },
          { name: SHEET_CLAIMS, ...claims },
        ],
        `${safeName}_ClinicalLiteratureReview.xlsx`,
      );
    } catch (error) {
      console.error("Combined export failed:", error);
      toast.error(error?.message || "Combined export failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClinicalEvalReportClick = async () => {
     if (!studyName) {
          toast.error("studyName is missing!");
          return;
      }

    setLoading(true,"Loading Summary… This may take a few minutes. Thank you for waiting.", "text-green-600");

    let keepAliveInterval;

      try {
      await RegulifyService.get_allowed_credits(5);
      
      const token = await getAuthToken();

      const ws = new WebSocket(WS_URL(token));
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        keepAliveInterval = startKeepAlive(ws);

        const payload = {
            product_name: study_name,
            override_flag: 1,
        };

        ws.send(JSON.stringify(payload));
        toast.info("Processing started...");
      };

      ws.onmessage = (event) => {
        const { data } = event;

        try {
          const parsed = JSON.parse(data);

          if (handleParsedMessage(parsed, { setLoading })) { return; }

          if (parsed.content && parsed.filename) {
              const byteCharacters = atob(parsed.content);
              const byteArray = new Uint8Array(
                [...byteCharacters].map((c) => c.charCodeAt(0))
              );

              const blob = new Blob([byteArray], {
                type:
                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              });

              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = parsed.filename;
              link.click();
          } else {
            console.log("Unexpected message format:", parsed);
          }
        } catch {
          console.log("Non-JSON message received:", data);
        }
      };

      ws.onerror = () => {
        console.error(WS_ERROR_MESSAGE);
        toast.error(WS_ERROR_MESSAGE);
        setLoading(false);
      };

      ws.onclose = () => {
        console.log("WebSocket closed");
        clearInterval(keepAliveInterval);
        setLoading(false);
      };
    } catch (error) {
      console.error(UNHANDELLED_ERROR_MESSAGE, error);
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-0 w-full flex-col overflow-y-scroll p-4">
      <div className="flex w-full flex-row items-center">
        <details className="dropdown" ref={detailsRef}>
          <summary
            className="btn tooltip tooltip-right m-1"
            data-tip="Select search term to filter study findings summary"
          >
            {selectedSearchTerm.name}{" "}
          </summary>
          {searchTerms && searchTerms.length > 1 && (
            <ul className="menu dropdown-content bg-base-100 rounded-box tooltip tooltip-center z-1 w-52 p-2 shadow-sm">
              <li>
                <a
                  onClick={() => {
                    setSelectedSearchTerm({
                      name: "All",
                    });
                    if (detailsRef.current) detailsRef.current.open = false;
                  }}
                >
                  All
                </a>
              </li>

              {searchTerms.map((searchTerm) => {
                if (searchTerm !== selectedSearchTerm) {
                  return (
                    <li key={searchTerm}>
                      <a
                        onClick={() => {
                          setSelectedSearchTerm({
                            name: searchTerm,
                            ...data.Per_Term_Metrics[searchTerm],
                          });
                          if (detailsRef.current)
                            detailsRef.current.open = false;
                        }}
                      >
                        {searchTerm}
                      </a>
                    </li>
                  );
                }
              })}
            </ul>
          )}
        </details>
        <div className="ml-auto flex gap-2">
          <button
            className="btn btn-primary tooltip tooltip-left"
            data-tip="Download Relevant Literatures, Appraisal, and Claims in one Excel file"
            onClick={handleExportCombinedData}
          >
            Export Combined Data
          </button>
          <button
            className="btn btn-primary"
            onClick={handleCreateClinicalEvalReportClick}
          >
            Generate Summarization Report
          </button>
        </div>
      </div>

      <div className="stats stats-vertical md:stats-horizontal mt-4 min-h-fit w-full shadow">
        <div className="stat">
          <div className="stat-title">Total Articles Found</div>
          <div className="stat-value">
            {selectedSearchTerm.name === "All"
              ? data?.Total_PMID_Occurrences
              : selectedSearchTerm.all_articles}
          </div>
        </div>
        <div className="stat">
          <div className="stat-title">Total Duplicates Found</div>
          <div className="stat-value">
            {selectedSearchTerm.name === "All" ? data?.Duplicate_PMID_Count : 0}
          </div>
        </div>

        <div className="stat">
          <div className="stat-title">Search Term Relevant Articles</div>
          <div className="stat-value">
            {getPropertySum("relevant_articles")}
          </div>
        </div>

        <div className="stat">
          <div className="stat-title">Device Relevant Articles</div>
          <div className="stat-value">
            {getPropertySum("claims_relevant_articles")}
          </div>
        </div>

        <div className="stat">
          <div className="stat-title">Non-Device Relevant Articles</div>
          <div className="stat-value">{getPropertySum("novel_articles")}</div>
        </div>
      </div>

      <div
        className={`mt-4 grid gap-4 ${hasCharts ? "lg:grid-cols-[minmax(0,7fr)_minmax(0,3fr)]" : "grid-cols-1"}`}
      >
        <div className="h-[calc(100vh-390px)] min-w-0 overflow-y-auto">
          <Table table={table} />
        </div>
        {hasCharts && (
          <div className="flex min-w-0 flex-col gap-4">
            <Chart
              chartData={relevantPmidPerTermChart}
              title={"Relevant PMIDs by search term"}
            />
            <Chart
              chartData={claimRelevantPmidPerTermChart}
              title={"Claims Relevant PMIDs by search term"}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Summary;
